import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Lock, Settings } from "lucide-react";
import { useAuth } from "../lib/auth";
import { errorMessage, rpc } from "../lib/supabase";
import { Button, Dialog, ErrorNote, Field, Input, useToast } from "./ui";

/** Presentación del alumno en el muro de su grupo: una línea sobre su proyecto y si aparece o no. */
export function ProfileDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { profile, refresh } = useAuth();
  const [tagline, setTagline] = useState("");
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    setTagline(profile?.wall_tagline ?? "");
    setHidden(profile?.wall_visible === false);
    setError("");
  }, [open, profile]);

  async function save() {
    setBusy(true);
    setError("");
    try {
      await rpc("set_wall_profile", { p_visible: !hidden, p_tagline: tagline });
      await refresh();
      toast("Tu presentación quedó guardada");
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={profile?.full_name ?? "Tu perfil"}>
      <div className="flex flex-col gap-16">
        <Link to="/ajustes" onClick={onClose}
          className="flex items-center justify-between gap-16 rounded-xl bg-carbon-ink px-16 py-[12px] text-bone-parchment no-underline hover:bg-graphite hover:text-bone-parchment">
          <span className="flex items-center gap-[10px]">
            <Settings size={18} aria-hidden />
            <span>
              <span className="block text-body font-medium">Ajustes del perfil</span>
              <span className="block text-caption text-mist">Meta semanal, recordatorios, nombre y contraseña</span>
            </span>
          </span>
          <span aria-hidden className="text-base16">›</span>
        </Link>
        <div className="border-t border-chalk" />
        <Field label="Tu presentación al grupo" hint={`Una línea sobre tu proyecto. ${tagline.length} de 90.`}>
          <Input value={tagline} maxLength={90} placeholder="Novela negra en Guadalajara" onChange={(e) => setTagline(e.target.value)} />
        </Field>
        <label className="flex items-center gap-8 text-body text-graphite">
          <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
          Ocultarme del muro
        </label>
        <p className="flex items-start gap-8 text-xs2 text-ashen">
          <Lock size={13} className="mt-[2px] shrink-0" aria-hidden />
          Tus compañeros solo ven tu nombre, esta línea, tu racha y tus insignias. Nunca tus textos ni cuántas palabras escribes.
        </p>
        <ErrorNote>{error}</ErrorNote>
        <div className="flex justify-end gap-8">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} loading={busy}>Guardar presentación</Button>
        </div>
      </div>
    </Dialog>
  );
}
